import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, RotateCcw, XCircle } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { creditLabel } from "@/components/AppShell";

export const NOT_COMPLETED = "Payment not completed. No credits were added.";
const FAILED = ["cancelled", "expired", "failed"];

function Card({ tone, icon: Icon, title, children, testId }) {
  const ring = tone === "ok" ? "border-emerald-500/30 bg-emerald-500/5" : tone === "bad" ? "border-red-500/30 bg-red-500/5" : "border-slate-800 bg-[#F2F2F7]/70";
  return (
    <div data-testid={testId} className={`rise mx-auto max-w-xl rounded-2xl border p-10 text-center ${ring}`}>
      <Icon className={`mx-auto mb-5 h-10 w-10 ${tone === "ok" ? "text-emerald-400" : tone === "bad" ? "text-red-400" : "animate-spin text-amber-400"}`} />
      <h1 data-testid="billing-return-title" className="font-display text-2xl font-bold text-slate-50">{title}</h1>
      <div className="mt-3 text-sm text-slate-400">{children}</div>
      <Link to="/billing" data-testid="back-to-billing-link" className="mt-8 inline-flex rounded-full border border-slate-700 px-5 py-2 text-sm text-slate-300 transition-colors hover:border-slate-500 hover:text-[#171A20]">Back to Billing</Link>
    </div>
  );
}

export function BillingSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const { setCredits } = useAuth();
  const [st, setSt] = useState(null);
  const [gaveUp, setGaveUp] = useState(false);
  const tries = useRef(0);
  const check = useCallback(async () => {
    try {
      const { data } = await api.get(`/billing/checkout/${sid}`);
      setSt(data);
      setCredits(data.balance);
      return data.status === "paid" || FAILED.includes(data.status);
    } catch (e) {
      setSt({ status: "error", error: errorMessage(e) });
      return true;
    }
  }, [sid, setCredits]);
  useEffect(() => {
    if (!sid) return undefined;
    let timer;
    let alive = true;
    const loop = async () => {
      if (!alive) return;
      if (await check()) return;
      tries.current += 1;
      if (tries.current >= 20) return setGaveUp(true);
      timer = setTimeout(loop, 1500);
    };
    loop();
    return () => { alive = false; clearTimeout(timer); };
  }, [sid, check]);

  if (!sid || st?.status === "error") return <Card tone="bad" icon={XCircle} title={NOT_COMPLETED} testId="payment-failed">{st?.error}</Card>;
  if (st?.status === "paid") {
    return (
      <Card tone="ok" icon={CheckCircle2} title={`Payment successful: ${st.credits} credits added`} testId="payment-success">
        New balance: <span data-testid="payment-new-balance" className="font-mono-num font-semibold text-amber-200">{creditLabel(st.balance)}</span>
      </Card>
    );
  }
  if (st && FAILED.includes(st.status)) return <Card tone="bad" icon={XCircle} title={NOT_COMPLETED} testId="payment-failed" />;
  return (
    <Card icon={Loader2} title="Confirming your payment…" testId="payment-pending">
      {gaveUp ? (
        <button data-testid="payment-check-again" onClick={() => { tries.current = 0; setGaveUp(false); check(); }}
          className="inline-flex items-center gap-2 text-amber-300 hover:underline"><RotateCcw className="h-4 w-4" /> Still waiting on Stripe. Check again</button>
      ) : "This usually takes a few seconds."}
    </Card>
  );
}

export function BillingCancel() {
  const [params] = useSearchParams();
  const tx = params.get("tx");
  const { setCredits, refreshCredits } = useAuth();
  const [st, setSt] = useState(null);
  useEffect(() => {
    if (tx) api.post(`/billing/checkout/cancel/${tx}`).then((r) => { setSt(r.data); setCredits(r.data.balance); }).catch(() => refreshCredits());
  }, [tx, setCredits, refreshCredits]);
  if (st?.status === "paid") {
    return (
      <Card tone="ok" icon={CheckCircle2} title={`Payment successful: ${st.credits} credits added`} testId="payment-success">
        New balance: <span data-testid="payment-new-balance" className="font-mono-num font-semibold text-amber-200">{creditLabel(st.balance)}</span>
      </Card>
    );
  }
  return <Card tone="bad" icon={XCircle} title={NOT_COMPLETED} testId="payment-cancelled">You can pick a pack again any time.</Card>;
}
